import { motion } from 'framer-motion';
import { LegacyRef, useState } from 'react';
import { CgMenuRightAlt } from 'react-icons/cg';
import { FaAngleDoubleRight, FaUser } from 'react-icons/fa';
import { FaBell } from 'react-icons/fa6';
import { IoLogOutOutline } from 'react-icons/io5';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuthContext } from '../../contexts/AuthProvider';
import { useNavbarContext } from '../../contexts/NavbarProvider';
import useAuthActions from '../../hooks/APIHooks/users/useAuthActions';
import useUserClickObserver from '../../hooks/useUserClickObserver';
import { Client, User } from '../../types/users';
import { showFullName, showPicture } from '../../utils/commonFunctions';
import { deleteToken } from '../../utils/cookieHandler';
import { fetchErrorMessage } from '../../utils/fetchErrorMessage';
import SkillBadge from './Badges/SkillBadge';
import HeaderNotification from './HeaderNotification';

const Header = ({ isApplicationPage }: { isApplicationPage?: boolean }) => {
  // integration of react hooks here
  const [showNotifications, setShowNotifications] = useState<boolean>(false);

  // integration of context hooks here
  const { showNavbar, setShowNavbar, isMobile, setShowMobileNavbar } =
    useNavbarContext();

  const { currentUser, setCurrentUserId, setCurrentUserType, currentUserType } =
    useAuthContext();

  // integration of react-router-dom hooks here
  const navigate = useNavigate();

  // integration of custom hooks here
  const selectRef = useUserClickObserver(setShowNotifications);

  // integration of API hooks here
  const { userLogoutHandler } = useAuthActions();

  // handler function to handle logout feature declared here
  const logoutHandler = async () => {
    try {
      await userLogoutHandler
        .mutateAsync()
        .then((value) => {
          console.log('LOG OUT RES: ', value);

          if (value.is_success) {
            deleteToken();

            setCurrentUserId(0);
            setCurrentUserType('');

            localStorage.clear();

            navigate('/');
            toast.success('Logout Successful!!');
          } else {
            toast.error(value.message || 'Logout Failed!!');
          }
          navigate('/');
        })
        .catch((error) => {
          navigate('/');

          deleteToken();

          setCurrentUserId(0);

          localStorage.clear();

          toast.error(fetchErrorMessage(error));
        });
    } catch (error) {
      toast.error('Something Went Wrong!!');
    }
  };

  // rendering header component here
  return (
    <motion.header
      initial={
        showNavbar
          ? isMobile || isApplicationPage
            ? { paddingLeft: 0 }
            : { paddingLeft: '260px' }
          : isMobile
          ? { paddingLeft: 0 }
          : { paddingLeft: '70px' }
      }
      animate={
        showNavbar
          ? isMobile || isApplicationPage
            ? { paddingLeft: 0 }
            : { paddingLeft: '260px' }
          : isMobile
          ? { paddingLeft: 0 }
          : { paddingLeft: '70px' }
      }
      className='font-lato fixed min-h-[80px] w-full top-0 py-3 bg-base-white shadow-academia-sm pl-0 lg:pl-[260px] z-[999]'
    >
      <nav
        className={`px-3 mx-auto flex items-center justify-between ${
          isApplicationPage
            ? 'w-full md:w-[90%] lg:w-4/5 max-w-[1500px] mx-auto md:px-0'
            : 'md:px-5'
        }`}
      >
        {!isApplicationPage ? (
          isMobile ? (
            <button
              onClick={() => setShowMobileNavbar((state) => !state)}
              className='hover:opacity-70 active:scale-95 duration-300'
            >
              <CgMenuRightAlt className='size-7 text-legal-dark-blue' />
            </button>
          ) : (
            <button
              onClick={() => setShowNavbar((state) => !state)}
              className='hover:opacity-70 active:scale-95 duration-300'
            >
              <CgMenuRightAlt className='size-7 text-legal-dark-blue' />
            </button>
          )
        ) : (
          <h1 className='text-xl md:text-2xl text-legal-dark-blue italic'>
            MyLegalites
          </h1>
        )}

        {currentUser && (
          <div className='flex items-center gap-1 md:gap-4'>
            {!isApplicationPage ? (
              <div
                ref={selectRef as LegacyRef<HTMLDivElement>}
                className='md:relative flex items-center justify-center mr-3'
              >
                <button
                  onClick={() => setShowNotifications((state) => !state)}
                  className='relative hover:opacity-70 active:scale-95 duration-300'
                >
                  <FaBell className='size-7 text-legal-dark-blue' />

                  <span className='absolute -top-1 text-xs font-semibold bg-base-orange text-base-white px-1 py-px rounded-full'>
                    30
                  </span>
                </button>

                <motion.ul
                  initial={{ height: 0 }}
                  animate={
                    showNotifications
                      ? { height: 'fit-content' }
                      : { height: 0 }
                  }
                  className='absolute w-[95%] md:w-[400px] left-1/2 -translate-x-1/2 top-[80%] md:top-full bg-base-white shadow-academia-sm rounded-xl mt-2 overflow-hidden'
                >
                  <HeaderNotification />
                  <HeaderNotification />
                  <HeaderNotification />
                  <HeaderNotification />
                  <HeaderNotification />

                  <div className='flex items-center justify-end mr-3 mb-2'>
                    <button
                      onClick={() => navigate('/notifications')}
                      className='flex items-center justify-center gap-1 text-base-purple font-medium hover:opacity-60 duration-300'
                    >
                      <span>See More</span> <FaAngleDoubleRight />
                    </button>
                  </div>
                </motion.ul>
              </div>
            ) : null}

            <div className='md:text-lg flex flex-col items-end italic'>
              <p className='font-medium text-base-dark-green capitalize text-xl'>
                {`${showFullName(
                  currentUser.first_name,
                  currentUser.middle_name,
                  currentUser.last_name,
                  currentUserType === 'client'
                )}`}
              </p>

              {currentUserType === 'client' ? (
                <div
                  title={`Current Balance: ${
                    (currentUser as Client).current_credits?.toLocaleString() ||
                    '0.00'
                  } Credits`}
                  className='w-fit flex items-center gap-1 border-2 border-base-purple text-base-purple bg-base-purple/20 rounded-full px-2 py-1 text-xs font-semibold'
                >
                  <div className='size-4'>
                    <img src='/assets/icons/coin.svg' alt='coin icon' />
                  </div>
                  <p>
                    {(
                      currentUser as Client
                    ).current_credits?.toLocaleString() || '0.00'}{' '}
                    Credits
                  </p>
                </div>
              ) : (
                <SkillBadge
                  badgeLabel={`${(currentUser as User).role_name}`}
                  color={`${
                    (currentUser as User).role_name === 'Admin'
                      ? '#845ADF'
                      : (currentUser as User).role_name === 'Associate'
                      ? '#00BCEF'
                      : '#FF8042'
                  }`}
                />
              )}
            </div>

            <Link
              title='View Profile'
              to={'/profile'}
              className={`hover:opacity-45 duration-300 rounded-full border-2 ${
                currentUserType === 'client'
                  ? 'border-base-cyan'
                  : (currentUser as User).role_name === 'Admin'
                  ? 'border-base-purple'
                  : (currentUser as User).role_name === 'Associate'
                  ? 'border-base-light-blue'
                  : 'border-base-orange'
              }`}
            >
              {currentUser?.profile_picture ? (
                <div className='size-12 md:size-16 rounded-full overflow-hidden'>
                  <img
                    className='h-full w-full object-cover'
                    src={showPicture(currentUser.profile_picture)}
                    height={0}
                    width={0}
                    sizes='100%'
                    alt={`Avatar of ${currentUser.username}`}
                  />
                </div>
              ) : (
                <div className='size-12 md:size-16 rounded-full bg-base-light-gray flex items-center justify-center'>
                  <FaUser className='size-7 md:size-9 text-gray-300' />
                </div>
              )}
            </Link>

            <button
              onClick={logoutHandler}
              title='Logout'
              className='hover:opacity-50 duration-300'
            >
              <IoLogOutOutline className='h-8 w-8 text-legal-dark-blue' />
            </button>
          </div>
        )}
      </nav>
    </motion.header>
  );
};

export default Header;
